import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { Subscription } from 'rxjs';
import { SocketStatus } from '../../data-models/socket-base.data-model';
import { AgentWsIdentityAtom, ChatConversationIdAtom } from '../../pages/ChatBot/hooks/chatActionHooks';
import { useAutoReconnect } from '../../pages/ChatBot/hooks/useAutoReconnect';
import { useReconnectOnTenantChange } from '../../pages/ChatBot/hooks/useReconnectOnTenantChange';
import { viewFromSavedFrame } from '../../pages/ChatBot/utils/viewSavedFrame';
import { putViewPFJ } from '../../pages/PortfolioOverview/state/ViewStateJ';
import {
  ChatSocketMessageType,
  ISocketConnectedResponse,
  SocketAssistantAttention,
  SocketViewSaved,
} from '../../schemas/agent-conversation.schema';
import { useToken } from '../../services/hooks/useToken';
import { HudsonWsService } from '../../services/HudsonWsService';
import { PermissionKey } from '../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../services/PermissionService';
import { readCustomerTenantId } from '../../services/state/AuthState';
import { useToastMessageState } from '../ToastMessage/ToastMessageProvider';
import {
  assistantAttentionAtom,
  assistantSocketHeldAtom,
  ATTENTION_MESSAGE,
} from './assistantAttentionStateJ';
import { ShowChatBotAtom } from './chatbot-state';
import { useAssistant } from './useAssistant';

/** A server that closes cleanly on arrival must not be met with a reconnect storm. */
const RECONNECT_AFTER_CLOSE_COOLDOWN_MS = 30_000;

/**
 * Keeps the assistant socket open for the whole session for the users whose agent can need
 * them while the panel is closed: Customer Success, the only users who engage Atlas. Atlas
 * event turns run only while the user has a socket, so with the panel's own lifecycle a
 * question from Atlas was seen only if the panel happened to be open on that conversation.
 * Everyone else connects while the panel is open, as before.
 *
 * An `assistant_attention` frame badges the header's assistant button and toasts, with an
 * Open action, unless the panel already shows that conversation; the panel never opens on
 * its own. Chat frames that arrive while the panel is closed are ignored: opening the
 * conversation fetches its history, which has the reply.
 */
export function AssistantSocket() {
  const held = PermissionService.get().hasPermission(PermissionKey.customerSuccessAll);
  const getToken = useToken();
  const setHeld = useSetAtom(assistantSocketHeldAtom);
  const setAgentWsIdentity = useSetAtom(AgentWsIdentityAtom);
  const { pushInfoToast } = useToastMessageState();
  const { openAssistant } = useAssistant();
  useReconnectOnTenantChange(held);

  const onAttention = useAtomCallback(
    useCallback(
      (get, set, frame: SocketAssistantAttention) => {
        // The agent sends it to every socket the user has open, and a Customer Success user keeps
        // tabs open on several customers: only the socket's own tenant is this tab's business.
        if (frame.tenant_id !== get(AgentWsIdentityAtom)?.tenantId) {
          return;
        }
        if (get(ShowChatBotAtom) && get(ChatConversationIdAtom) === frame.conversation_id) {
          return;
        }
        set(assistantAttentionAtom, {
          conversationId: frame.conversation_id,
          reason: frame.reason,
          title: frame.title ?? null,
        });
        pushInfoToast({
          title: frame.title ?? 'Atlas',
          message: ATTENTION_MESSAGE[frame.reason],
          buttonLabel: 'Open',
          buttonAction: () => openAssistant({ conversation: frame.conversation_id }),
        });
      },
      [pushInfoToast, openAssistant]
    )
  );

  // The panel handles a view the agent saved while it is open (useChat); this covers it closed.
  const onViewSaved = useAtomCallback(
    useCallback((get, set, frame: SocketViewSaved) => {
      if (get(ShowChatBotAtom) || frame.tenant_id !== get(AgentWsIdentityAtom)?.tenantId) {
        return;
      }
      const view = viewFromSavedFrame(frame);
      if (view) {
        void set(putViewPFJ, view);
      }
    }, [])
  );

  useEffect(() => {
    if (!held) {
      return;
    }
    setHeld(true);
    let cancelled = false;
    let lastReconnectAt = 0;
    const subs: Subscription[] = [];
    HudsonWsService.initService(getToken, readCustomerTenantId).then((service) => {
      if (cancelled) {
        return;
      }
      subs.push(
        // The panel reads the identity off this frame too, but only while it is open.
        service.listen<ISocketConnectedResponse>(ChatSocketMessageType.connectionEstablished, (update) => {
          setAgentWsIdentity(
            update.user ? { userId: update.user.id, tenantId: update.user.tenant_id } : null
          );
        }),
        service.listen<SocketAssistantAttention>(ChatSocketMessageType.assistantAttention, onAttention),
        service.listen<SocketViewSaved>(ChatSocketMessageType.viewSaved, onViewSaved),
        // A clean close is final for the service, and the panel's next initService would build a
        // new instance these listeners are not on; reconnect this one in place instead. A socket
        // that errored out is useAutoReconnect's, below.
        service.socketStatus.subscribe((status) => {
          if (
            status === SocketStatus.closed &&
            Date.now() - lastReconnectAt > RECONNECT_AFTER_CLOSE_COOLDOWN_MS
          ) {
            lastReconnectAt = Date.now();
            void service.reconnect();
          }
        })
      );
    });
    return () => {
      cancelled = true;
      // Before the disconnect: its own clean close is not one to answer.
      subs.forEach((sub) => sub.unsubscribe());
      setHeld(false);
      HudsonWsService.get()?.disconnect();
      setAgentWsIdentity(null);
    };
  }, [held, getToken, onAttention, onViewSaved, setHeld, setAgentWsIdentity]);

  // Declared after the effect above: the status it subscribes to on mount exists only once that
  // effect has created the service.
  useAutoReconnect(held);

  // The badge clears once the panel shows that conversation.
  const shown = useAtomValue(ShowChatBotAtom);
  const conversationId = useAtomValue(ChatConversationIdAtom);
  const attention = useAtomValue(assistantAttentionAtom);
  const setAttention = useSetAtom(assistantAttentionAtom);
  useEffect(() => {
    if (attention && shown && conversationId === attention.conversationId) {
      setAttention(null);
    }
  }, [attention, shown, conversationId, setAttention]);

  return null;
}
